import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { LogOut, RefreshCw, Search, Users, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import {
  loadHelperSession,
  clearHelperSession,
} from "@/pages/FestivalHelperRedeem";
import { AssignPartnerModal, canPairPlayer, groupedPlayerIds } from "@/components/AssignPartnerModal";

// Iter431/433/437 — OPS Assist (internal role `roster_ops_helper`).
// Panel-only roster view. Shows every player in Registration or
// Resting status, sorted by last name then first name (toggle to
// first-name sort). Search bar filters live. A-Z jump rail on the
// right lets a helper tap a letter to scroll directly to that
// section. Two actions per row (Mark Available / Assign to
// Coaching). Right-click a row on desktop for the picker.

const ROSTER_STATUSES = ["imported", "arrived", "available", "resting"];
const STATUS_TONE = {
  imported: { label: "Imported", cls: "text-[#B0B0B0] border-[#525252]" },
  arrived: { label: "Arrived", cls: "text-[#66C2FF] border-[#66C2FF]" },
  available: { label: "Available", cls: "text-[#CCFF00] border-[#CCFF00]" },
  resting: { label: "Resting", cls: "text-[#FFB020] border-[#FFB020]" },
};

const BAND_DOT = {
  green: "bg-[#4CAF50]",
  unrated: "bg-[#4CAF50]",
  blue: "bg-[#66C2FF]",
  red: "bg-[#FF4500]",
  spare: "bg-[#525252]",
};

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

function bandDot(band) {
  const k = (band || "unrated").toLowerCase();
  return BAND_DOT[k] || BAND_DOT.unrated;
}

function fmtHHmm(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function fmtLevel(level) {
  if (!level) return "";
  const k = String(level).toLowerCase();
  if (k === "u35") return "U3.5";
  if (k === "o35") return "O3.5";
  return level;
}

function firstLetter(player, sortMode = "last") {
  const primary = sortMode === "first"
    ? (player.first_name || player.last_name)
    : (player.last_name || player.first_name);
  const raw = (primary || "?").trim();
  const ch = raw.charAt(0).toUpperCase();
  return /[A-Z]/.test(ch) ? ch : "#";
}

function useRightClick(onFire) {
  const onContextMenu = useCallback((evt) => {
    evt.preventDefault();
    onFire(evt.currentTarget);
  }, [onFire]);
  return { onContextMenu };
}

function flattenCoachingSessions(courts) {
  const out = [];
  for (const c of courts || []) {
    const queue = Array.isArray(c.coaching_slot_queue) ? c.coaching_slot_queue : [];
    if (queue.length > 0) {
      for (const s of queue) {
        if (!s?.coaching_slot_at) continue;
        out.push({
          court_id: c.id,
          court_name: c.name,
          coaching_group_id: s.coaching_group_id,
          name: s.coaching_session_name || "Coaching",
          coach: s.coaching_coach_name || "TBC",
          level: fmtLevel(s.coaching_level),
          slot_at: s.coaching_slot_at,
          signup_ids: s.coaching_signup_ids || [],
          cap: s.coaching_max_players || c.coaching_max_players || 6,
        });
      }
    } else if (c.coaching_slot_at) {
      out.push({
        court_id: c.id,
        court_name: c.name,
        coaching_group_id: c.coaching_group_id,
        name: c.coaching_session_name || "Coaching",
        coach: c.coaching_coach_name || "TBC",
        level: fmtLevel(c.coaching_level),
        slot_at: c.coaching_slot_at,
        signup_ids: c.coaching_signup_ids || [],
        cap: c.coaching_max_players || 6,
      });
    }
  }
  const cutoff = Date.now() - 30 * 60 * 1000;
  return out
    .filter((s) => new Date(s.slot_at).getTime() >= cutoff)
    .sort((a, b) => String(a.slot_at || "").localeCompare(String(b.slot_at || "")));
}

function CoachingPickerModal({ player, sessions, onClose, onPick }) {
  if (!player) return null;
  return (
    <div
      className="fixed inset-0 z-[95] bg-black/80 flex items-end sm:items-center justify-center p-3"
      data-testid="courts-ops-picker"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FFB020]"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#262626]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#FFB020]">Assign to coaching</div>
            <div className="text-sm text-white truncate mt-0.5">{player.first_name} {player.last_name}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="courts-ops-picker-close"
            className="p-1 border border-[#262626] text-[#E0E0E0] hover:text-white hover:border-white"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="max-h-[60vh] overflow-y-auto">
          {sessions.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[#B0B0B0]" data-testid="courts-ops-picker-empty">
              No scheduled coaching sessions.
            </div>
          ) : (
            <ul className="divide-y divide-[#1F1F1F]">
              {sessions.map((s, idx) => {
                const already = (s.signup_ids || []).includes(player.id);
                const full = (s.signup_ids || []).length >= (s.cap || 6);
                return (
                  <li key={`${s.court_id}-${s.coaching_group_id || idx}`}>
                    <button
                      type="button"
                      disabled={already || full}
                      onClick={() => onPick(s)}
                      data-testid={`courts-ops-picker-session-${idx}`}
                      className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#141414] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white font-bold truncate">{s.name}</div>
                        <div className="mt-0.5 text-[11px] text-[#E0E0E0]">
                          <span>{s.court_name}</span>
                          <span className="text-[#B0B0B0]"> · </span>
                          <span className="text-white">{fmtHHmm(s.slot_at)}</span>
                          <span className="text-[#B0B0B0]"> · </span>
                          <span>Coach {s.coach}</span>
                          {s.level && (
                            <>
                              <span className="text-[#B0B0B0]"> · </span>
                              <span className="text-[#CCFF00]">{s.level}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-[10px] uppercase tracking-widest text-[#FFB020]">
                        {already ? "Signed up" : full ? "Full" : `${(s.signup_ids || []).length}/${s.cap}`}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function PlayerRow({ player, onSetStatus, onAssign, onPartner, sectionRef }) {
  // Iter434 · Right-click on desktop opens the coaching picker. Mobile
  // uses the inline "Assign to Coaching" button below — no long-press.
  const bind = useRightClick(() => onAssign(player));
  const tone = STATUS_TONE[player.status] || STATUS_TONE.imported;
  return (
    <li
      ref={sectionRef}
      className="border border-[#1F1F1F] bg-[#101010] px-3 py-3 select-none"
      data-testid={`courts-ops-row-${player.id}`}
      {...bind}
    >
      <div className="flex items-center gap-3">
        <span
          className={`shrink-0 inline-block w-2.5 h-2.5 rounded-full ${bandDot(player.colour_band)}`}
          aria-hidden
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate" data-testid={`courts-ops-name-${player.id}`}>
            {player.first_name} {player.last_name}
          </div>
          <div className={`mt-0.5 inline-block text-[9px] uppercase tracking-widest px-1.5 py-[1px] border ${tone.cls}`}>
            {tone.label}
          </div>
        </div>
      </div>
      {/* Iter434 · Single action row — Mark Available (Resting is the
           default between games) + Assign to Coaching (opens picker
           on tap; right-click on the row also opens it on desktop). */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onSetStatus(player, "available")}
          disabled={player.status === "available"}
          data-testid={`courts-ops-available-${player.id}`}
          className="px-2 py-2 text-[11px] uppercase tracking-widest border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Mark Available
        </button>
        <button
          type="button"
          onClick={() => onAssign(player)}
          data-testid={`courts-ops-assign-${player.id}`}
          className="px-2 py-2 text-[11px] uppercase tracking-widest border border-[#FFB020] text-[#FFB020] hover:bg-[#FFB020] hover:text-black"
        >
          Assign to Coaching
        </button>
        {onPartner && (
          <button
            type="button"
            onClick={() => onPartner(player)}
            data-testid={`courts-ops-partner-${player.id}`}
            className="col-span-2 px-2 py-2 text-[11px] uppercase tracking-widest border border-[#66C2FF] text-[#66C2FF] hover:bg-[#66C2FF] hover:text-black transition-colors"
          >
            Assign partner
          </button>
        )}
      </div>
    </li>
  );
}

export default function FestivalCourtsOps() {
  const { fid } = useParams();
  const [players, setPlayers] = useState(null);
  const [courts, setCourts] = useState([]);
  const [err, setErr] = useState(null);
  const [picker, setPicker] = useState(null);
  const [allInMode, setAllInMode] = useState(false);
  const [playRequests, setPlayRequests] = useState([]);
  const [partnerFor, setPartnerFor] = useState(null);
  const [search, setSearch] = useState("");
  // Iter436 — Sort toggle: sort roster + jump rail by Last name
  // (default) or First name. Persist choice in localStorage so the
  // helper's preference sticks across refreshes.
  const [sortMode, setSortMode] = useState(() => {
    try {
      const v = localStorage.getItem("rl_courts_ops_sort");
      return v === "first" ? "first" : "last";
    } catch {
      return "last";
    }
  });
  useEffect(() => {
    try { localStorage.setItem("rl_courts_ops_sort", sortMode); } catch { /* ignore */ }
  }, [sortMode]);
  const session = loadHelperSession();
  const letterRefs = useRef({});

  // Iter445 — Defence-in-depth kickout for the OPS Assist page. The
  // api.js 401 interceptor is the primary kickout path, but iOS
  // Safari in a backgrounded tab has been seen to silently drop the
  // hard navigation. If the helper session is gone on ANY render
  // (initial mount, re-render after a wipe, or when the component
  // regains focus), bounce to the redeem screen ourselves — no
  // amount of stale UI is better than the user staring at a dead
  // page.
  useEffect(() => {
    if (!session) {
      if (typeof window === "undefined") return;
      const target = fid ? `/festival/${fid}` : "/login";
      if (window.location.pathname !== target) {
        try { window.location.replace(target); }
        catch { try { window.location.href = target; } catch { /* ignore */ } }
      }
    }
  }, [session, fid]);

  const load = useCallback(async () => {
    try {
      const [pRes, cRes, prRes] = await Promise.all([
        api.get(`/festival/${fid}/players`),
        api.get(`/festival/${fid}/courts`),
        api.get(`/festival/${fid}/play-requests`).catch(() => ({ data: { requests: [] } })),
      ]);
      setAllInMode(cRes.data?.config?.assignment_mode === "all_in");
      setPlayRequests(prRes.data?.requests || []);
      setPlayers(Array.isArray(pRes.data?.items) ? pRes.data.items : (Array.isArray(pRes.data?.players) ? pRes.data.players : (Array.isArray(pRes.data) ? pRes.data : [])));
      setCourts(Array.isArray(cRes.data?.courts) ? cRes.data.courts : (Array.isArray(cRes.data) ? cRes.data : []));
      setErr(null);
    } catch (e) {
      // Iter446 — Suppress the red error strip when the 401 is a
      // helper-PIN single-device kickout. The api.js interceptor
      // is already firing the amber toast and tearing the page
      // down with a hard redirect; flashing "Couldn't load roster"
      // for 150ms looks like something went wrong when it hasn't.
      if (e?.__helperKickout) {
        setErr(null);
        return;
      }
      setErr(formatApiError(e, "Couldn't load roster"));
    }
  }, [fid]);

  // Iter441 — Robust polling for iOS Safari. `setInterval` alone
  // gets throttled or killed when the phone backgrounds or the tab
  // is inactive, which is why single-device kickouts weren't
  // reaching Phone A. Two-part fix:
  //  1. The interval still runs when foregrounded so an active
  //     helper sees status changes every 5 s.
  //  2. `visibilitychange` + `pageshow` + `focus` trigger an
  //     immediate `load()` so returning to the tab picks up an
  //     external kickout on the very next paint (401 with "used on
  //     another device" is then caught by the axios interceptor
  //     in `lib/api.js` and redirects to the redeem screen).
  useEffect(() => {
    let alive = true;
    const safeLoad = () => { if (alive && !document.hidden) load(); };
    safeLoad();
    const iv = setInterval(safeLoad, 5000);
    window.addEventListener("visibilitychange", safeLoad);
    window.addEventListener("pageshow", safeLoad);
    window.addEventListener("focus", safeLoad);
    return () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener("visibilitychange", safeLoad);
      window.removeEventListener("pageshow", safeLoad);
      window.removeEventListener("focus", safeLoad);
    };
  }, [load]);

  const setStatus = async (player, newStatus) => {
    try {
      await api.patch(`/festival/${fid}/players/${player.id}`, { status: newStatus });
      // Iter451 — Silent action. The single-device kickout toast
      // from api.js stays; every other toast on OPS Assist was
      // judged noise by the desk. On a thrown kickout 401 the
      // interceptor's amber toast + hard redirect still fires.
      load();
    } catch (e) {
      // Swallow silently per Iter451 — kickout 401s are already
      // surfaced by api.js; other errors would've been toasted
      // but the desk explicitly asked for a quiet surface.
      if (e?.__helperKickout) return;
    }
  };

  const assignCoaching = async (player, s) => {
    try {
      await api.post(`/festival/${fid}/courts/${s.court_id}/coaching/signup`, {
        player_id: player.id,
        coaching_group_id: s.coaching_group_id || null,
      });
      setPicker(null);
      load();
    } catch (e) {
      if (e?.__helperKickout) return;
      // Iter451 — Silent failure for parity with Mark Available.
      // Picker stays open on failure so the operator can retry.
    }
  };

  const signOut = () => {
    clearHelperSession();
    window.location.href = "/";
  };
  // Iter453 — Sign-out confirmation parity. Check-In already gates
  // its Sign out behind a "Are you sure you are done?" prompt
  // (Iter450); mirror it here on OPS Assist so a stray thumb-tap
  // on a tablet mounted at the desk can't nuke an active shift.
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  // Iter433 — Filter + sort alphabetically by last name then first
  // name. `roster` is the pre-search sorted list; `visibleRoster`
  // applies the live search filter.
  const roster = useMemo(() => {
    if (!Array.isArray(players)) return [];
    const key = sortMode === "first"
      ? (p) => `${(p.first_name || "").toLowerCase()} ${(p.last_name || "").toLowerCase()}`
      : (p) => `${(p.last_name || "").toLowerCase()} ${(p.first_name || "").toLowerCase()}`;
    return players
      .filter((p) => ROSTER_STATUSES.includes(p.status))
      .sort((a, b) => key(a).localeCompare(key(b)));
  }, [players, sortMode]);

  const visibleRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter((p) => {
      const full = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
      return full.includes(q);
    });
  }, [roster, search]);

  // Iter433 — Set of letters that have at least one player after
  // filtering, so the A-Z rail can dim letters with nothing to jump
  // to and skip disabling taps on active ones.
  const activeLetters = useMemo(() => {
    const s = new Set();
    for (const p of visibleRoster) s.add(firstLetter(p, sortMode));
    return s;
  }, [visibleRoster, sortMode]);

  const jumpTo = (letter) => {
    const el = letterRefs.current[letter];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const sessions = useMemo(() => flattenCoachingSessions(courts), [courts]);
  const grouped = useMemo(() => groupedPlayerIds(playRequests), [playRequests]);

  // Iter433 — Track the letter of the first player in each group so
  // we can attach the jump-target ref to the correct row without an
  // extra DOM pass at render time.
  const firstPlayerPerLetter = useMemo(() => {
    const seen = new Set();
    const map = {};
    for (const p of visibleRoster) {
      const L = firstLetter(p, sortMode);
      if (!seen.has(L)) {
        seen.add(L);
        map[p.id] = L;
      }
    }
    return map;
  }, [visibleRoster, sortMode]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="courts-ops-root">
      <div className="max-w-md mx-auto px-4 pt-6">
        <header className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB020]">OPS Assist</div>
            <h1 className="h-display text-2xl mt-1 leading-tight truncate" data-testid="courts-ops-label">
              {session?.label || "Roster"}
            </h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={load}
              data-testid="courts-ops-refresh"
              className="p-2 border border-[#262626] text-[#E0E0E0] hover:text-white hover:border-white"
              aria-label="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setConfirmSignOut(true)}
              data-testid="courts-ops-signout"
              className="p-2 border border-[#262626] text-[#E0E0E0] hover:text-[#FF4500] hover:border-[#FF4500]"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>
        <p className="mt-2 text-[11px] text-[#E0E0E0] leading-relaxed">
          Tap <span className="text-[#FFB020]">Assign to Coaching</span> on a row (or right-click on desktop) to pick a session.
        </p>

        {/* Iter433 — Live search input. Filters on first + last name
             (case-insensitive) as the helper types. */}
        <div className="mt-3 relative">
          <Search className="w-4 h-4 text-[#B0B0B0] absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="courts-ops-search"
            placeholder="Search players by name…"
            className="w-full pl-8 pr-8 py-2 text-sm bg-[#0A0A0A] border border-[#262626] focus:border-[#FFB020] focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              data-testid="courts-ops-search-clear"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-[#B0B0B0] hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Iter436 — Sort-by toggle: Last name (default) or First
             name. Drives roster order AND the A-Z jump rail. */}
        <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest">
          <span className="text-[#B0B0B0]">Sort by</span>
          <div className="inline-flex border border-[#262626]" role="group" data-testid="courts-ops-sort-toggle">
            <button
              type="button"
              onClick={() => setSortMode("first")}
              aria-pressed={sortMode === "first"}
              data-testid="courts-ops-sort-first"
              className={`px-3 py-1 ${sortMode === "first" ? "bg-[#FFB020] text-black" : "text-[#E0E0E0] hover:text-white"}`}
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setSortMode("last")}
              aria-pressed={sortMode === "last"}
              data-testid="courts-ops-sort-last"
              className={`px-3 py-1 border-l border-[#262626] ${sortMode === "last" ? "bg-[#FFB020] text-black" : "text-[#E0E0E0] hover:text-white"}`}
            >
              Last
            </button>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-[#FFB020] via-[#525252] to-transparent mt-4 mb-4" />

        {err && (
          <div className="mb-4 border border-[#FF4500] bg-[#4B1010]/40 px-3 py-2 text-xs text-white" data-testid="courts-ops-error">
            {err}
          </div>
        )}

        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#E0E0E0] mb-2">
          <span className="inline-flex items-center gap-1">
            <Users className="w-3 h-3" /> {visibleRoster.length} on roster
          </span>
          <span data-testid="courts-ops-sessions-count">{sessions.length} coaching session{sessions.length === 1 ? "" : "s"}</span>
        </div>

        <div className="flex gap-2 items-start">
          <div className="flex-1 min-w-0">
            {players === null ? (
              <div className="text-sm text-[#B0B0B0] py-8 text-center" data-testid="courts-ops-loading">Loading roster…</div>
            ) : visibleRoster.length === 0 ? (
              <div className="border border-[#262626] bg-[#141414] p-6 text-center" data-testid="courts-ops-empty">
                <div className="text-sm text-[#E0E0E0]">
                  {search ? `No matches for "${search}"` : "No players in Registration or Resting."}
                </div>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="courts-ops-list">
                {visibleRoster.map((p) => (
                  <PlayerRow
                    key={p.id}
                    player={p}
                    onSetStatus={setStatus}
                    onAssign={(pl) => setPicker(pl)}
                    onPartner={allInMode && canPairPlayer(p, grouped) ? (pl) => setPartnerFor(pl) : null}
                    sectionRef={
                      firstPlayerPerLetter[p.id]
                        ? (el) => {
                            if (el) letterRefs.current[firstPlayerPerLetter[p.id]] = el;
                          }
                        : undefined
                    }
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Iter433 — Vertical A-Z jump rail on the right edge. Tap a
               letter to scroll the list to the first player under
               that letter. Letters with no players in the filtered
               view render dimmed and disabled. */}
          <nav
            aria-label="Jump to letter"
            data-testid="courts-ops-alpha-rail"
            className="shrink-0 sticky top-4 flex flex-col items-center gap-[1px] text-[9px] font-mono select-none"
          >
            {ALPHABET.map((L) => {
              const active = activeLetters.has(L);
              return (
                <button
                  key={L}
                  type="button"
                  onClick={() => active && jumpTo(L)}
                  disabled={!active}
                  data-testid={`courts-ops-alpha-${L}`}
                  className={`w-5 h-5 flex items-center justify-center ${active ? "text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black" : "text-[#3A3A3A] cursor-default"}`}
                >
                  {L}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="mt-10 text-[10px] uppercase tracking-widest text-[#B0B0B0] text-center">
          Powered by Rally-Lab
        </div>
      </div>

      <CoachingPickerModal
        player={picker}
        sessions={sessions}
        onClose={() => setPicker(null)}
        onPick={(s) => assignCoaching(picker, s)}
      />
      {partnerFor && (
        <AssignPartnerModal
          festivalId={fid}
          player={partnerFor}
          players={players || []}
          playRequests={playRequests}
          onClose={() => setPartnerFor(null)}
          onDone={load}
        />
      )}

      {/* Iter453 — Sign-out confirmation (matches Iter450 Check-In
          flow). Tap backdrop or Cancel to dismiss; Confirm fires the
          existing `signOut()` which clears the helper session and
          hard-navigates home. */}
      {confirmSignOut && (
        <div
          className="fixed inset-0 z-[96] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setConfirmSignOut(false)}
          data-testid="courts-ops-signout-modal"
        >
          <div
            className="w-full max-w-sm bg-[#0A0A0A] border-2 border-[#FFB020] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="label-mono text-[#FFB020] text-[10px] mb-2">END SESSION</div>
            <h2 className="h-display text-xl leading-snug mb-5" data-testid="courts-ops-signout-title">
              Are you sure you are done?
            </h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmSignOut(false)}
                data-testid="courts-ops-signout-cancel"
                className="flex-1 px-4 py-3 text-[11px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:text-white hover:border-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setConfirmSignOut(false); signOut(); }}
                data-testid="courts-ops-signout-confirm"
                className="flex-1 px-4 py-3 text-[11px] uppercase tracking-widest font-bold border border-[#FFB020] bg-[#FFB020] text-black hover:bg-white hover:border-white"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
